import { cpSync, existsSync, mkdirSync, rmSync } from "node:fs";
import { basename, dirname, join, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import { ensureMcpRegistered, ensureCaiHooks, ensureLearnHook } from "./mcp/install.js";
import { enableLearn, isLearnEnabled } from "./learn/recorder.js";
import { generateRules } from "./rules.js";

const UPDATEABLE_SCAFFOLD_ENTRIES = [
  ".tool-configs",
  "CLAUDE.md",
  "LICENSE",
  "README.md",
  "SETUP.md",
  "SYNC.md",
  "dist",
  "mascot",
  "package.json",
  "screenshots",
  "setup.sh",
  "sync.sh",
  "tsup.config.ts",
  "update.sh",
  "visualize.sh",
] as const;

const PROTECTED_SCAFFOLD_ENTRIES = [
  "AGENTS.md",
  "ROUTER.md",
  "context",
  "patterns",
] as const;

export interface UpdateResult {
  scaffoldRoot: string;
  updated: string[];
  missingInSource: string[];
  protected: string[];
  setupRan?: boolean;
  mcpStatus?: "registered" | "already_registered" | "claude_not_found" | "failed";
  rulesGenerated?: number;
}

export function runUpdate(opts: {
  scaffoldRoot: string;
  sourceRoot?: string;
}): UpdateResult {
  const sourceRoot = opts.sourceRoot ?? getPackageRoot();
  const scaffoldRoot = resolve(opts.scaffoldRoot);

  if (!existsSync(scaffoldRoot)) {
    throw new Error(`No scaffold directory found at ${scaffoldRoot}`);
  }

  const scaffoldDirName = basename(scaffoldRoot);
  if (scaffoldDirName !== ".cai" && scaffoldDirName !== ".context-condensing") {
    throw new Error(`Refusing to update non-scaffold directory: ${scaffoldRoot}`);
  }

  const updated: string[] = [];
  const missingInSource: string[] = [];

  for (const entry of UPDATEABLE_SCAFFOLD_ENTRIES) {
    const from = join(sourceRoot, entry);
    const to = join(scaffoldRoot, entry);
    if (!existsSync(from)) {
      missingInSource.push(entry);
      continue;
    }

    if (existsSync(to)) {
      rmSync(to, { recursive: true, force: true });
    } else {
      mkdirSync(dirname(to), { recursive: true });
    }

    cpSync(from, to, { recursive: true });
    updated.push(entry);
  }

  const projectRoot = resolve(scaffoldRoot, "..");
  const mcp = ensureMcpRegistered(projectRoot);
  ensureCaiHooks(projectRoot);

  // Enable correction recording if it isn't already (matches `cai setup` default).
  if (!isLearnEnabled(projectRoot)) {
    enableLearn(projectRoot);
    ensureLearnHook(projectRoot);
  }

  // Regenerate .claude/rules/ from updated scaffold
  let rulesGenerated = 0;
  try {
    const config = { projectRoot, scaffoldRoot, subProjectRoot: projectRoot, settings: {} };
    const rules = generateRules(config);
    rulesGenerated = rules.written.length;
  } catch {
    // Non-fatal
  }

  return {
    scaffoldRoot,
    updated,
    missingInSource,
    protected: [...PROTECTED_SCAFFOLD_ENTRIES],
    mcpStatus: mcp.status,
    rulesGenerated,
  };
}

export function printUpdateResult(result: UpdateResult): void {
  console.log();
  console.log("cai update — infrastructure refreshed");
  console.log();
  console.log(`✔ ${result.scaffoldRoot}`);
  console.log(`  ${result.updated.length} entries updated`);
  if (result.updated.length > 0) {
    for (const entry of result.updated) {
      console.log(`    · ${entry}`);
    }
  }
  console.log();
  console.log(`  Preserved (your edits are safe): ${result.protected.join(", ")}`);
  console.log(`  ℹ These files contain your project-specific configuration and are never overwritten.`);
  console.log();

  // Rules + MCP status
  if (result.rulesGenerated && result.rulesGenerated > 0) {
    console.log(`✔ ${result.rulesGenerated} path-scoped rules refreshed from updated scaffold`);
  }
  if (result.mcpStatus === "registered") {
    console.log("✔ MCP server registered — Claude can now read your scaffold context directly");
  } else if (result.mcpStatus === "already_registered") {
    console.log("✔ MCP server active");
  } else if (result.mcpStatus === "claude_not_found") {
    console.log("  ℹ MCP not connected — run: cai mcp install  (only needed once after installing Claude)");
  }
  console.log();

  if (result.setupRan) {
    console.log("  ℹ Run cai check to verify everything is in order.");
  } else {
    console.log("  ℹ Run cai check to verify, or cai update --setup to re-run AI setup.");
  }
  console.log();
}

function getPackageRoot(): string {
  const cliDist = fileURLToPath(import.meta.url);
  return resolve(cliDist, "..", "..");
}
